import { useSearchParams } from "react-router-dom";
import Loader from "Shared/Loader/Loader";
import {
  useCancelLoan,
  useEditLoanId,
  useSelectedOrderDetails,
  useUpdateBankTransferStatus,
  useUpdateCashStatus,
  useUpdateChequeStatus,
} from "store/backend/assets";
import {
  ErrorMessage,
  PrimaryButton,
  Table,
  Dialog,
  Textfield,
  ErrorButton,
  Text,
  SecondaryButton,
} from "widgets";
import {
  CASH_PAYMENT_METHOD,
  CHEQUE_PAYMENT_METHOD,
  LOAN_STATUSES,
  PAYMENT_STATUSES,
  CHEQUE_STATUS,
  PAYMENT_MODE,
  LOAN_PAYMENT_METHOD,
  BANK_TRANSFER_PAYMENT_METHOD,
  BANK_TRANSFER_STATUS_IDS,
  PAYMENT_MODE_LABEL,
} from "constants/config";
import { useState } from "react";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";

import styles from "./EditPayments.module.scss";

const handleDialogClose = (setOpenDialogContent) => () => {
  setOpenDialogContent(null);
};

const getColumns = () => [
  { label: "Amount", field: "amount" },
  { label: "Status", field: "status" },
  { label: "Method", field: "method" },
  { label: "Action", field: "action" },
];

const handleOnEditPayment = (
  setOpenEditLoanDialogContent,
  setLoanApplicationNumber,
  loan,
  setLoanPaymentId
) => {
  setLoanApplicationNumber(loan.loan_id);
  setOpenEditLoanDialogContent(true);
  setLoanPaymentId(loan.id);
};

const handleOnDeletePayment = (
  setOpenConfirmLoanCancelDialogContent,
  loan_id
) => {
  setOpenConfirmLoanCancelDialogContent(loan_id);
};

const isEditable = (loan) => {
  if (!loan || !loan.status) return false;
  return loan.status.id === LOAN_STATUSES.CREATED;
};

const isCancellable = (loan) => {
  if (!loan || !loan.status) return false;
  return loan.status.id === LOAN_STATUSES.CREATED;
};
const getPaymentStatus = (payment) => {
  if (
    payment.cheque_details &&
    Object.keys(payment.cheque_details).length !== 0
  ) {
    return payment.cheque_details.status.id === CHEQUE_STATUS.VERIFIED
      ? "ACKNOWLEDGED"
      : payment.cheque_details.status.name;
  }
  return payment.status.name;
};
const getRows = (
  payments,
  setOpenEditLoanDialogContent,
  setLoanApplicationNumber,
  setOpenConfirmLoanCancelDialogContent,
  setLoanPaymentId,
  setPaymentToCancel,
  setPaymentCancelType
) => {
  return payments.map((payment) => {
    const isPaymentCreated = payment.status?.id === PAYMENT_STATUSES.CREATED;
    const isCash = payment.method?.id === CASH_PAYMENT_METHOD;
    const isCashCreated =
      isCash && payment.cash_details?.status?.id === LOAN_STATUSES.CREATED;
    const isCheque = payment.method?.id === CHEQUE_PAYMENT_METHOD;
    const isChequeCreated =
      isCheque && payment.cheque_details?.status?.id === CHEQUE_STATUS.CREATED;
    const isChequeVerified =
      isCheque &&
      payment.cheque_details &&
      payment.cheque_details.status?.id === CHEQUE_STATUS.VERIFIED;
    const isLoan = payment.method?.id === LOAN_PAYMENT_METHOD;
    const isLoanCreated =
      isLoan && payment.loan_details?.status?.id === LOAN_STATUSES.CREATED;
    const isBankTransfer = payment.method?.id === BANK_TRANSFER_PAYMENT_METHOD;
    const isBankTransferCreated =
      isBankTransfer &&
      payment.bank_transfer_details?.status?.id === PAYMENT_STATUSES.CREATED;

    return {
      amount: payment.amount,
      method: payment.method?.name,
      status: getPaymentStatus(payment),
      action: isLoanCreated ? (
        <div className={styles.actionButtons}>
          <PrimaryButton
            disabled={!isEditable(payment.loan_details)}
            onClick={() =>
              handleOnEditPayment(
                setOpenEditLoanDialogContent,
                setLoanApplicationNumber,
                payment.loan_details,
                setLoanPaymentId
              )
            }
          >
            Edit
          </PrimaryButton>
          <ErrorButton
            disabled={!isCancellable(payment.loan_details)}
            onClick={() =>
              handleOnDeletePayment(
                setOpenConfirmLoanCancelDialogContent,
                payment.loan_details.loan_id
              )
            }
          >
            Cancel
          </ErrorButton>
        </div>
      ) : isCashCreated ? (
        <div className={styles.actionButtons}>
          <ErrorButton
            onClick={() => {
              setPaymentToCancel(payment);
              setPaymentCancelType(PAYMENT_MODE.CASH);
            }}
          >
            Cancel
          </ErrorButton>
        </div>
      ) : isPaymentCreated && isChequeCreated && !isChequeVerified ? (
        <div className={styles.actionButtons}>
          <ErrorButton
            onClick={() => {
              setPaymentToCancel(payment);
              setPaymentCancelType(PAYMENT_MODE.CHEQUE);
            }}
          >
            Cancel
          </ErrorButton>
        </div>
      ) : isBankTransferCreated ? (
        <div className={styles.actionButtons}>
          <ErrorButton
            onClick={() => {
              setPaymentToCancel(payment);
              setPaymentCancelType(PAYMENT_MODE.BANK_TRANSFER);
            }}
          >
            Cancel
          </ErrorButton>
        </div>
      ) : (
        <div className={styles.actionButtons}>N/A</div>
      ),
    };
  });
};

const handleEditLoanId = (
  loanApplicationNumber,
  loanPaymentId,
  editLoanId,
  openSnackbar,
  setOpenEditLoanDialogContent
) => {
  const payload = {
    loan_detail_id: loanPaymentId,
    new_loan_id: loanApplicationNumber,
  };
  editLoanId(payload, {
    onSuccess: () => {
      setOpenEditLoanDialogContent(null);
      openSnackbar("Loan Id Successfully updated.");
      setTimeout(() => window.location.reload(), 1000);
    },
    onError: (error) =>
      openSnackbar(error?.message ?? String(error), "error"),
  });
};

const payloadForCashPayment = (paymentId, markedBy) => ({
  payment_id: paymentId,
  marked_by: markedBy,
  payment_status_id: PAYMENT_STATUSES.REJECTED_BY_INFINITY_LEARN,
});

const payloadForChequePayment = (paymentId, markedBy) => ({
  payment_id: paymentId,
  marked_by: markedBy,
  updated_cheque_status_id: CHEQUE_STATUS.CANCELLED,
});

const payloadForBankTransferPayment = (paymentId, markedBy) => ({
  payment_id: paymentId,
  marked_by: markedBy,
  new_bank_transfer_status_id: BANK_TRANSFER_STATUS_IDS.CANCELLED,
});

const getCancelConfirmationMessage = (paymentCancelType, paymentToCancel) => {
  const label = PAYMENT_MODE_LABEL[paymentCancelType];

  if (
    paymentCancelType === PAYMENT_MODE.BANK_TRANSFER &&
    paymentToCancel &&
    paymentToCancel.bank_transfer_details
  ) {
    const referenceId =
      paymentToCancel.bank_transfer_details.reference_transaction_id;

    return (
      <>
        Are you sure you want to cancel {label} payment with Reference
        Transaction ID:{" "}
        <span className={styles.highlightedId}>{referenceId || "N/A"}</span> ?
      </>
    );
  }

  const id =
    paymentToCancel && paymentToCancel.id
      ? paymentToCancel.id.toString()
      : "N/A";

  return (
    <>
      Are you sure you want to cancel {label} payment with ID:{" "}
      <span className={styles.highlightedId}>{id}</span> ?
    </>
  );
};

const EditPayments = () => {
  const [searchParams] = useSearchParams();
  const order_id = searchParams.get("order_id");
  const countryId = searchParams.get("countryId");
  const [salesPersonId] = useState(localStorage.getItem("sales_person_id"));
  const [openEditLoanDialogContent, setOpenEditLoanDialogContent] =
    useState(null);
  const [loanApplicationNumber, setLoanApplicationNumber] = useState("");
  const [loanPaymentId, setLoanPaymentId] = useState("");
  const [
    openConfirmLoanCancelDialogContent,
    setOpenConfirmLoanCancelDialogContent,
  ] = useState(null);
  const [paymentToCancel, setPaymentToCancel] = useState(null);
  const [paymentCancelType, setPaymentCancelType] = useState(null);
  const { openSnackbar } = useSnackbarContext();

  const {
    isPending: isFetchAllPaymentsLoading,
    data: selectedOrderDetails,
    isError: isErrorOnPayments,
    error: errorMessageOnPayments,
  } = useSelectedOrderDetails(order_id, { countryId });

  const payments = selectedOrderDetails?.payment_details || [];

  const { mutate: editLoanId, isPending: isEditLoanIdLoading } =
    useEditLoanId();
  const { mutate: cancelLoan, isPending: isCancelLoanLoading } =
    useCancelLoan();
  const { mutate: updateCashStatus, isPending: isUpdateCashStatusLoading } =
    useUpdateCashStatus();
  const { mutate: updateChequeStatus, isPending: isUpdateChequeStatusLoading } =
    useUpdateChequeStatus();

  const {
    mutate: updateBankTransferStatus,
    isPending: isUpdateBankTransferStatusLoading,
  } = useUpdateBankTransferStatus();

  if (isFetchAllPaymentsLoading) return <Loader />;
  if (isErrorOnPayments)
    return <ErrorMessage>{errorMessageOnPayments.message}</ErrorMessage>;

  return (
    <>
      <div className={styles.header}>EDIT PAYMENTS</div>
      <div className={styles.paymentTable}>
        {payments.length > 0 ? (
          <Table
            columns={getColumns()}
            rows={getRows(
              payments,
              setOpenEditLoanDialogContent,
              setLoanApplicationNumber,
              setOpenConfirmLoanCancelDialogContent,
              setLoanPaymentId,
              setPaymentToCancel,
              setPaymentCancelType
            )}
          />
        ) : (
          <ErrorMessage>No payments done.</ErrorMessage>
        )}
      </div>

      <Dialog
        open={openEditLoanDialogContent !== null}
        handleClose={handleDialogClose(setOpenEditLoanDialogContent)}
      >
        <Text>Please provide the updated Loan ID</Text>
        <div className={styles.editLoanIdContainer}>
          <Textfield
            value={loanApplicationNumber}
            handleOnChange={(event) =>
              setLoanApplicationNumber(event.target.value)
            }
            height="auto"
          />
          <div className={styles.actionButtons}>
            <SecondaryButton
              onClick={handleDialogClose(setOpenEditLoanDialogContent)}
            >
              Go Back
            </SecondaryButton>
            <PrimaryButton
              disabled={isEditLoanIdLoading}
              onClick={() =>
                handleEditLoanId(
                  loanApplicationNumber,
                  loanPaymentId,
                  editLoanId,
                  openSnackbar,
                  setOpenEditLoanDialogContent
                )
              }
            >
              {isEditLoanIdLoading ? "Updating" : "Update"}
            </PrimaryButton>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={openConfirmLoanCancelDialogContent !== null}
        handleClose={handleDialogClose(setOpenConfirmLoanCancelDialogContent)}
      >
        <div className={styles.cancelLoanContainer}>
          <Text>
            Are you sure you want to cancel loan with Id:{" "}
            <span className={styles.highlightedId}>
              {openConfirmLoanCancelDialogContent}
            </span>
            ?
          </Text>
          <div className={styles.actionButtons}>
            <PrimaryButton
              onClick={handleDialogClose(setOpenConfirmLoanCancelDialogContent)}
            >
              No,Go Back
            </PrimaryButton>
            <ErrorButton
              disabled={isCancelLoanLoading}
              onClick={() =>
                cancelLoan(openConfirmLoanCancelDialogContent, {
                  onSuccess: () => {
                    setOpenConfirmLoanCancelDialogContent(null);
                    openSnackbar("Loan Successfully cancelled.");
                    setTimeout(() => window.location.reload(), 1000);
                  },
                  onError: (error) =>
                    openSnackbar(error?.message ?? String(error), "error"),
                })
              }
            >
              {isCancelLoanLoading ? "Cancelling" : "Yes,Cancel Loan"}
            </ErrorButton>
          </div>
        </div>
      </Dialog>

      <Dialog
        open={!!paymentToCancel && !!paymentToCancel.id}
        handleClose={() => {
          setPaymentToCancel(null);
          setPaymentCancelType(null);
        }}
      >
        <div className={styles.cancelLoanContainer}>
          {paymentToCancel ? (
            <Text>
              {getCancelConfirmationMessage(paymentCancelType, paymentToCancel)}
            </Text>
          ) : null}

          <div className={styles.actionButtons}>
            <SecondaryButton
              onClick={() => {
                setPaymentToCancel(null);
                setPaymentCancelType(null);
              }}
            >
              No,Go Back
            </SecondaryButton>
            <PrimaryButton
              disabled={
                paymentCancelType === PAYMENT_MODE.CASH
                  ? isUpdateCashStatusLoading
                  : paymentCancelType === PAYMENT_MODE.CHEQUE
                  ? isUpdateChequeStatusLoading
                  : paymentCancelType === PAYMENT_MODE.BANK_TRANSFER
                  ? isUpdateBankTransferStatusLoading
                  : false
              }
              onClick={() => {
                const payload =
                  paymentCancelType === PAYMENT_MODE.CASH
                    ? payloadForCashPayment(paymentToCancel.id, salesPersonId)
                    : paymentCancelType === PAYMENT_MODE.CHEQUE
                    ? payloadForChequePayment(paymentToCancel.id, salesPersonId)
                    : payloadForBankTransferPayment(
                        paymentToCancel.id,
                        salesPersonId
                      );

                const handler =
                  paymentCancelType === PAYMENT_MODE.CASH
                    ? updateCashStatus
                    : paymentCancelType === PAYMENT_MODE.CHEQUE
                    ? updateChequeStatus
                    : paymentCancelType === PAYMENT_MODE.BANK_TRANSFER
                    ? updateBankTransferStatus
                    : null;

                handler(payload, {
                  onSuccess: () => {
                    openSnackbar(
                      `${
                        PAYMENT_MODE_LABEL[paymentCancelType] || ""
                      } payment successfully cancelled.`
                    );
                    setTimeout(() => window.location.reload(), 1000);
                  },
                  onError: (error) =>
                    openSnackbar(error?.message ?? String(error), "error"),
                });

                setPaymentToCancel(null);
                setPaymentCancelType(null);
              }}
            >
              Yes,Cancel Payment
            </PrimaryButton>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default EditPayments;
